import { createAsyncThunk } from "@reduxjs/toolkit";
import { trackPromise } from "react-promise-tracker";
import { fetchWrapper } from "_utils";
import { ERR_MSG, PORTAL_KEY as name } from "constants/labels";
import { blobBrowserDownload } from "_utils/files/blobBrowserDownload";

const baseUrlSd = `${process.env.REACT_APP_API_URL_SUPPLIER_DIVERISTY}/api/supplier-diversity`;
const baseUrlAccount = `${process.env.REACT_APP_API_URL}/api/Account`;

/** Define thunks here */
const apiCall = async (promise, rejectWithValue, validate = false) => {
  try {
    const response = await trackPromise(promise);

    if (validate) {
      const { Succeeded, Errors, Message } = response;
      if (!Succeeded && Errors?.length) throw new Error(Message);
    }

    return response;
  } catch (error) {
    return rejectWithValue(error);
  }
};

export const getSupplierDiversityDetailsApi = createAsyncThunk(
  `${name}/getSupplierDiversityDetailsApi`,
  async (_, { rejectWithValue }) =>
    apiCall(
      fetchWrapper.get(`${baseUrlSd}/registered-supplier`),
      rejectWithValue
    )
);

export const postSupplierDiversityExpiringInApi = createAsyncThunk(
  `${name}/postSupplierDiversityExpiringInApi`,
  async (payload, { rejectWithValue }) =>
    apiCall(
      fetchWrapper.post(`${baseUrlSd}/expiring-in`, { Data: payload }),
      rejectWithValue,
      true
    )
);

export const getSupplierDiversitySummaryCountApi = createAsyncThunk(
  `${name}/getSupplierDiversitySummaryCountApi`,
  async (_, { rejectWithValue }) =>
    apiCall(
      fetchWrapper.get(`${baseUrlSd}/supplier-summary-count`),
      rejectWithValue
    )
);

export const getSupplierDiversityStateApi = createAsyncThunk(
  `${name}/getSupplierDiversityStateApi`,
  async (_, { rejectWithValue }) =>
    apiCall(
      fetchWrapper.get(`${baseUrlAccount}/supplier-diversity/state`),
      rejectWithValue
    )
);

export const getSupplierDiversityCategoryApi = createAsyncThunk(
  `${name}/getSupplierDiversityCategoryApi`,
  async (_, { rejectWithValue }) =>
    apiCall(
      fetchWrapper.get(`${baseUrlAccount}/GetBusinessCategory`),
      rejectWithValue
    )
);

export const getSupplierDiversityExpiringSupplierApi = createAsyncThunk(
  `${name}/getSupplierDiversityExpiringSupplierApi`,
  async (_, { rejectWithValue }) =>
    apiCall(fetchWrapper.get(`${baseUrlSd}/expiring-supplier`), rejectWithValue)
);

export const postRegisteredSupplierFilterApi = createAsyncThunk(
  `${name}/postRegisteredSupplierFilterApi`,
  async (payload, { rejectWithValue }) =>
    apiCall(
      fetchWrapper.post(`${baseUrlSd}/registered-supplier`, { Data: payload }),
      rejectWithValue,
      true
    )
);

export const postExpiringSupplierApi = createAsyncThunk(
  `${name}/postExpiringSupplierApi`,
  async (payload, { rejectWithValue }) =>
    apiCall(
      fetchWrapper.post(`${baseUrlSd}/expiring-supplier`, { Data: payload }),
      rejectWithValue,
      true
    )
);

export const getSupplierDiveristyAsyncApi = createAsyncThunk(
  `${name}/getSupplierDiveristyAsyncApi`,
  async (userId, { rejectWithValue }) =>
    apiCall(
      fetchWrapper.get(
        `${baseUrlAccount}/GetRegisterSupplierDiversity/Async/${userId}`
      ),
      rejectWithValue
    )
);

export const getBusinessCategoryApi = createAsyncThunk(
  `${name}/getBusinessCategory`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await trackPromise(
        fetchWrapper.get(`${baseUrlAccount}/GetBusinessCategory`)
      );
      return response;
    } catch (error) {
      rejectWithValue(error);
    }
  }
);

export const getClassificationApi = createAsyncThunk(
  `${name}/getClassification`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await trackPromise(
        fetchWrapper.get(`${baseUrlAccount}/GetClassification`)
      );
      return response;
    } catch (error) {
      rejectWithValue(error);
    }
  }
);

export const getDocumentTypeApi = createAsyncThunk(
  `${name}/getDocumentType`,
  async (portalKey, { rejectWithValue }) => {
    try {
      const response = await trackPromise(
        fetchWrapper.get(
          `${baseUrlAccount}/GetDocumentType?portalKey=${portalKey}`
        )
      );
      return response;
    } catch (error) {
      rejectWithValue(error);
    }
  }
);

export const getStateApi = createAsyncThunk(
  `${name}/getState`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await trackPromise(
        fetchWrapper.get(`${baseUrlAccount}/supplier-diversity/state`)
      );
      return response;
    } catch (error) {
      rejectWithValue(error);
    }
  }
);

export const getAgencyApi = createAsyncThunk(
  `${name}/getAgency`,
  async (stateId, { rejectWithValue }) => {
    try {
      const response = await trackPromise(
        fetchWrapper.post(
          `${baseUrlAccount}/supplier-diversity/agency?stateId=${stateId}`
        )
      );
      return response;
    } catch (error) {
      rejectWithValue(error);
    }
  }
);

export const registerSupplierApi = createAsyncThunk(
  `${name}/registerSupplier`,
  async (payload, { rejectWithValue }) => {
    try {
      const response = await trackPromise(
        fetchWrapper.post(`${baseUrlAccount}/Register-SD`, { Data: payload })
      );
      return response;
    } catch (error) {
      rejectWithValue(error);
    }
  }
);

export const updateSupplierApi = createAsyncThunk(
  `${name}/updateSupplier`,
  async (payload, { rejectWithValue }) => {
    try {
      const response = await trackPromise(
        fetchWrapper.put(`${baseUrlAccount}/UpdateRegister-SD`, {
          Data: payload,
        })
      );
      return response;
    } catch (error) {
      rejectWithValue(error);
    }
  }
);

export const getSupplierAdminApi = createAsyncThunk(
  `${name}/getSupplierAdmin`,
  async (id, { rejectWithValue }) => {
    try {
      const response = await trackPromise(
        fetchWrapper.get(
          `${baseUrlAccount}/GetRegisterSupplierDiversityAsync/${id}`
        )
      );
      return response;
    } catch (error) {
      rejectWithValue(error);
    }
  }
);

export const getSupplierApi = createAsyncThunk(
  `${name}/getSupplier`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await trackPromise(
        fetchWrapper.get(
          `${baseUrlAccount}/supplier-diversity/registration-details`
        )
      );
      return response;
    } catch (error) {
      rejectWithValue(error);
    }
  }
);

export const registerSupplierDownloadApi = createAsyncThunk(
  `${name}/registerSupplierDownloadApi`,
  async (payload, { rejectWithValue }) =>
    apiCall(
      fetchWrapper.post(`${baseUrlSd}/registered-supplier/report`, {
        Data: payload,
      }),
      rejectWithValue,
      true
    )
);

export const supplierFileDownloadApi = createAsyncThunk(
  `${name}/supplierFileDownloadApi`,
  async (fileName, { rejectWithValue }) => {
    try {
      const { blob, headers } = await fetchWrapper.getFile(
        `${baseUrlAccount}/supplier-diversity/download-file?filename=${encodeURIComponent(fileName)}`
      );
      blobBrowserDownload(blob, headers, fileName);
      return true;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
